// La cuenta conectada vive solo en este navegador. El token nunca se guarda
// en ningún servidor: sessionStorage (se borra al cerrar la pestaña) o, si el
// usuario lo pide, localStorage.

import type { Account, AccountStore } from '../src/broker/routes.ts'
import { QvaPayClient, QvaPayError } from '../src/qvapay/client.ts'

export const TOKEN_KEY = 'tp.token'
const SID = 'browser'
// Bloqueada en Terminal P2P: así de seguido se vuelve a probar (al levantarlo, entra sola)
export const BLOCKED_RETRY_MS = 60_000

export interface TokenStorage {
	session: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
	local: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

// La marca del módulo Pro (la licencia con que se descargó; el terminal la manda al relé) es de la cuenta que lo cargó:
// en cuanto cambia la cuenta se borra, para que la nueva nunca salga con la marca de otra (sería una «copia» para el
// servidor). El módulo se va del todo al recargar, cuando pro.js ve que la nueva cuenta no tiene Pro
const forgetProMark = () => void delete (globalThis as { TPProMark?: unknown }).TPProMark

export class BrowserAccounts implements AccountStore {
	private readonly storage: TokenStorage
	private readonly baseUrl: string
	private acc: Account | null = null
	// El token de la cuenta en memoria: si en el almacenamiento ya hay otro (o ninguno), se desconectó o cambió en otra pestaña
	private token: string | null = null
	private loading: Promise<Account | null> | null = null
	// El token que el relé dio por bloqueado y cuándo: el token se queda guardado y un rato no se vuelve a preguntar
	private block: { token: string; at: number; err: QvaPayError } | null = null
	private readonly now: () => number

	constructor(opts: { storage: TokenStorage; baseUrl: string; now?: () => number }) {
		this.storage = opts.storage
		this.baseUrl = opts.baseUrl
		this.now = opts.now ?? Date.now
	}

	newSid() {
		return SID
	}

	// Valida el token contra QvaPay antes de guardarlo
	async connect(_sid: string, token: string, opts: { remember?: boolean } = {}): Promise<Account> {
		// Antes de la primera petición con el token nuevo (si no vale, la cuenta de antes sigue, solo que sin marca)
		forgetProMark()
		const client = new QvaPayClient({ token, dryRun: false, baseUrl: this.baseUrl })
		const me = await client.me()
		this.clear()
		this.block = null
		safe(() => (opts.remember ? this.storage.local : this.storage.session).setItem(TOKEN_KEY, token))
		this.acc = { sid: SID, me, client, print: await tokenPrint(token) }
		// Sin almacenamiento (bloqueado, modo privado) el token vive solo en memoria y no hay otra pestaña que mirar
		this.token = this.stored() === token ? token : null
		return this.acc
	}

	async get(): Promise<Account | null> {
		if (this.acc && (this.token === null || this.stored() === this.token)) return this.acc
		// Otra pestaña cambió o quitó la cuenta
		if (this.acc) forgetProMark()
		this.acc = null
		const b = this.block
		if (b && b.token === this.stored() && this.now() - b.at < BLOCKED_RETRY_MS) throw b.err
		// Varias llamadas a la vez comparten la misma carga
		this.loading ??= this.load().finally(() => (this.loading = null))
		return this.loading
	}

	// La cuenta con la que se lee el libro: solo la del propio usuario
	reader() {
		return this.get().catch(() => null)
	}

	async disconnect() {
		forgetProMark()
		this.acc = null
		this.token = null
		this.block = null
		this.clear()
	}

	// El relé dijo que esta cuenta está bloqueada: se olvida en memoria (el token sigue guardado)
	blocked(_sid: string | undefined, err: QvaPayError) {
		const token = this.token ?? this.stored()
		this.acc = null
		this.token = null
		if (token) this.block = { token, at: this.now(), err }
	}

	private stored() {
		return safe(() => this.storage.session.getItem(TOKEN_KEY) ?? this.storage.local.getItem(TOKEN_KEY))
	}

	private async load(): Promise<Account | null> {
		const token = this.stored()
		if (!token) return null
		const client = new QvaPayClient({ token, dryRun: false, baseUrl: this.baseUrl })
		try {
			this.acc = { sid: SID, me: await client.me(), client, print: await tokenPrint(token) }
			this.token = token
			return this.acc
		} catch (err) {
			// Token revocado o caducado: se borra de este navegador
			if (err instanceof QvaPayError && err.status === 401) this.clear()
			if (err instanceof QvaPayError && err.code === 'TP_ACCOUNT_BLOCKED') this.block = { token, at: this.now(), err }
			throw err
		}
	}

	private clear() {
		safe(() => this.storage.session.removeItem(TOKEN_KEY))
		safe(() => this.storage.local.removeItem(TOKEN_KEY))
	}
}

// Huella del token para el plan Pro: HMAC-SHA256 con una etiqueta fija. Identifica el dispositivo ante
// nuestro servidor sin revelar el token (no se puede invertir) y nadie puede calcularla sin el token
export async function tokenPrint(token: string) {
	const enc = new TextEncoder()
	const key = await crypto.subtle.importKey('raw', enc.encode('terminalp2p-pro'), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
	const sig = await crypto.subtle.sign('HMAC', key, enc.encode(token))
	return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// El almacenamiento puede fallar (modo privado, cuota, bloqueado): nunca rompe la app
function safe<T>(fn: () => T): T | null {
	try {
		return fn()
	} catch {
		return null
	}
}
